import React, { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// 30-minute time slots with friendly 12-hour labels (e.g. "12:30 PM")
const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2), m = i % 2 === 0 ? "00" : "30";
  const value = `${String(h).padStart(2, "0")}:${m}`;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const label = `${h12}:${m} ${h < 12 ? "AM" : "PM"}`;
  return { value, label };
});

const EMPTY_ITEM = { name: "", pax: 0, quantity: 1, uom: "pax", package_name: "", category: "main", notes: "" };
const UOMS = ["pax", "pcs", "stick", "tray", "set", "kg", "bottle"];
const blank = {
  invoice_name: "", address: "", event_date: "", job_title: "", theme: "",
  items: [{ ...EMPTY_ITEM }],
  customer_phone: "", customer_email: "",
  delivery_fee: 0,
  event_lat: null, event_lng: null,
  // Carried silently (set by package selection or kept from the order being edited)
  package_name: "", total_amount: 0, pax: 0, packages_pricing: [],
};

export default function OrderFormDialog({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [pkgPick, setPkgPick] = useState(null); // { pkg, checked: {menu_item_id: bool} }
  const [dishQ, setDishQ] = useState(""); // search filter for the dish dropdowns
  const [catalog, setCatalog] = useState([]);   // menu items
  const [packages, setPackages] = useState([]); // menu packages
  const [categories, setCategories] = useState([]); // menu categories
  const [jobTitles, setJobTitles] = useState([]); // past job titles for autocomplete
  const [addrSug, setAddrSug] = useState([]); // address autocomplete suggestions
  const addrTimer = useRef(null);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/menu/items", { params: { active_only: true } }).catch(() => ({ data: [] })),
      api.get("/menu/packages", { params: { active_only: true } }).catch(() => ({ data: [] })),
      api.get("/menu/categories").catch(() => ({ data: [] })),
      api.get("/orders/job-titles").catch(() => ({ data: [] })),
    ]).then(([ri, rp, rc, rj]) => { setCatalog(ri.data || []); setPackages(rp.data || []); setCategories(rc.data || []); setJobTitles(rj.data || []); });
  }, [open]);

  useEffect(() => {
    if (editing) {
      // Convert ISO event_date to local datetime-local format
      let evDate = editing.event_date || "";
      if (evDate) {
        try {
          const d = new Date(evDate);
          const off = d.getTimezoneOffset();
          evDate = new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
        } catch (e) {
          console.warn("Could not parse event_date — falling back to raw value:", e?.message);
        }
      }
      setForm({
        invoice_name: editing.customer_name || "",
        address: editing.event_location || "",
        event_date: evDate,
        job_title: editing.job_title || "",
        theme: editing.theme || "",
        items: (editing.items && editing.items.length > 0) ? editing.items.map((i) => ({ ...EMPTY_ITEM, ...i })) : [{ ...EMPTY_ITEM }],
        customer_phone: editing.customer_phone || "",
        customer_email: editing.customer_email || "",
        delivery_fee: editing.delivery_fee || 0,
        event_lat: editing.event_lat ?? null,
        event_lng: editing.event_lng ?? null,
        package_name: editing.package_name || "",
        total_amount: editing.total_amount || 0,
        pax: editing.pax || 0,
        packages_pricing: editing.packages_pricing || [],
      });
    } else if (open) {
      setForm(blank);
    }
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Address autocomplete via OpenStreetMap Nominatim (free, no API key)
  const onAddressChange = (v) => {
    setForm((f) => ({ ...f, address: v, event_lat: null, event_lng: null }));
    clearTimeout(addrTimer.current);
    if (!v || v.length < 4) { setAddrSug([]); return; }
    addrTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=my&q=${encodeURIComponent(v)}`);
        const list = await r.json();
        setAddrSug(Array.isArray(list) ? list : []);
      } catch (e) { console.warn("Address lookup failed:", e?.message); }
    }, 450);
  };
  const pickAddress = (s) => {
    setForm((f) => ({ ...f, address: s.display_name, event_lat: parseFloat(s.lat), event_lng: parseFloat(s.lon) }));
    setAddrSug([]);
  };

  // Split event_date ("YYYY-MM-DDTHH:mm") into date + time for the friendly picker
  const datePart = (form.event_date || "").slice(0, 10);
  const timePart = (form.event_date || "").slice(11, 16) || "12:00";
  const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const updateItem = (i, k, v) => {
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const dq = dishQ.trim().toLowerCase();
  const filteredCatalog = dq
    ? catalog.filter((c) => (c.name || "").toLowerCase().includes(dq) || (c.name_cn || "").includes(dishQ.trim()))
    : catalog;

  const applyPackage = () => {
    const { pkg, checked } = pkgPick;
    const isSet = pkg.pricing_mode === "set";
    const pax = Number(form.pax) || pkg.min_pax || 0;
    const newItems = pkg.items.filter((pi) => checked[pi.menu_item_id]).map((pi) => {
      const it = catalog.find((x) => x.id === pi.menu_item_id);
      if (!it) return null;
      const qty = isSet ? 1 : Math.max(1, Math.round((pax || 1) * (pi.quantity_factor || 1)));
      return { name: it.name, pax: isSet ? 0 : pax, quantity: qty, uom: isSet ? "set" : "pax", package_name: pkg.name, category: it.category, notes: "" };
    }).filter(Boolean);
    if (newItems.length === 0) { toast.error("Pick at least one dish"); return; }
    setForm((f) => {
      // Append to existing dishes (multiple packages can stack); skip duplicates & empty rows
      const existing = f.items.filter((i) => i.name);
      const merged = [...existing, ...newItems.filter((n) => !existing.find((e) => e.name === n.name && (e.package_name || "") === n.package_name))];
      const addTotal = isSet ? (pkg.price_per_pax || 0) : (pax > 0 && pkg.price_per_pax > 0 ? pax * pkg.price_per_pax : 0);
      const already = f.package_name && f.package_name !== "Custom";
      const pkgName = already
        ? (f.package_name.includes(pkg.name) ? f.package_name : `${f.package_name} + ${pkg.name}`)
        : pkg.name;
      return { ...f, package_name: pkgName, items: merged, total_amount: (Number(f.total_amount) || 0) + addTotal, pax,
        packages_pricing: [
          ...(f.packages_pricing || []).filter((p) => p.package_name !== pkg.name),
          { package_name: pkg.name, pricing_mode: isSet ? "set" : "pax", unit_price: pkg.price_per_pax || 0, quantity: 1, pax: isSet ? 0 : pax },
        ] };
    });
    setPkgPick(null);
    toast.success(`Added "${pkg.name}" (${newItems.length} dishes) — you can apply another package on top`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!datePart) { toast.error("Please pick a date"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_name: form.invoice_name,
        event_location: form.address,
        event_lat: form.event_lat,
        event_lng: form.event_lng,
        event_date: new Date(`${datePart}T${timePart}`).toISOString(),
        job_title: form.job_title,
        theme: form.theme,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email,
        delivery_fee: Number(form.delivery_fee) || 0,
        items: form.items.filter((i) => i.name).map((i) => ({
          ...i, pax: Number(i.pax) || 0, quantity: Number(i.quantity) || 1,
        })),
        package_name: form.package_name || "Custom",
        total_amount: Number(form.total_amount) || 0,
        pax: Number(form.pax) || 0,
        packages_pricing: form.packages_pricing || [],
      };
      if (editing) {
        await api.patch(`/orders/${editing.id}`, payload);
        toast.success("Order updated");
      } else {
        await api.post("/orders", payload);
        toast.success("Order created");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit Order" : "Create New Order"}</DialogTitle>
          <DialogDescription className="sr-only">{editing ? "Modify the details of this existing order." : "Key in the invoice name, address, menu selection and time to create a new order."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Invoice Name" wide>
              <Input required value={form.invoice_name} onChange={(e) => set("invoice_name", e.target.value)} placeholder="e.g. Tan Family Wedding / ABC Sdn Bhd" data-testid="order-invoice-name" className="bg-[#050505] border-[#2a2a2a]" />
            </FormField>
            <FormField label="Job Title (used in WhatsApp messages)" wide>
              <Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} list="job-title-options" placeholder="e.g. Wedding Dinner Buffet @ Grand Ballroom — type new or pick a past title" data-testid="order-job-title" className="bg-[#050505] border-[#2a2a2a]" />
              <datalist id="job-title-options">
                {jobTitles.map((t) => <option key={t} value={t} />)}
              </datalist>
            </FormField>
            <FormField label="Event Theme" wide>
              <Input value={form.theme} onChange={(e) => set("theme", e.target.value)} placeholder="e.g. Rustic Garden / Gatsby Gold / Traditional Red" data-testid="order-theme" className="bg-[#050505] border-[#2a2a2a]" />
            </FormField>
            <FormField label="Address" wide>
              <div className="relative">
                <Input required value={form.address} onChange={(e) => onAddressChange(e.target.value)}
                  placeholder="Full address — start typing to search (building, street, postcode, city)"
                  autoComplete="off" data-testid="order-address" className="bg-[#050505] border-[#2a2a2a]" />
                {addrSug.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded-sm shadow-xl max-h-52 overflow-y-auto" data-testid="address-suggestions">
                    {addrSug.map((s, i) => (
                      <button key={s.place_id || i} type="button" onClick={() => pickAddress(s)} data-testid={`address-sug-${i}`}
                        className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-[#141414] hover:text-[#D4AF37] border-b border-[#1a1a1a] last:border-0">
                        {s.display_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {form.event_lat != null && (
                <p className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1" data-testid="address-pinned">
                  ✓ Exact location pinned ({Number(form.event_lat).toFixed(5)}, {Number(form.event_lng).toFixed(5)}) — Google Maps & Waze will navigate precisely
                </p>
              )}
            </FormField>
            <FormField label="Phone (WhatsApp)">
              <Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="e.g. 60123456789" data-testid="order-customer-phone" className="bg-[#050505] border-[#2a2a2a]" />
            </FormField>
            <FormField label="Email (optional)">
              <Input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} placeholder="customer@email.com" data-testid="order-customer-email" className="bg-[#050505] border-[#2a2a2a]" />
            </FormField>
            <FormField label="Date & Time" wide>
              <div className="flex gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" data-testid="order-date-btn"
                      className={`flex-1 justify-start bg-[#050505] border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm font-normal ${!datePart ? "text-zinc-500" : ""}`}>
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {datePart
                        ? new Date(`${datePart}T00:00`).toLocaleDateString("en-MY", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#0a0a0a] border-[#2a2a2a] text-white" align="start">
                    <Calendar
                      mode="single"
                      selected={datePart ? new Date(`${datePart}T00:00`) : undefined}
                      defaultMonth={datePart ? new Date(`${datePart}T00:00`) : new Date()}
                      onSelect={(d) => {
                        if (!d) return;
                        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                        set("event_date", `${iso}T${timePart}`);
                      }}
                      data-testid="order-date-calendar"
                    />
                  </PopoverContent>
                </Popover>
                <select
                  value={timePart}
                  onChange={(e) => set("event_date", `${datePart || todayIso()}T${e.target.value}`)}
                  data-testid="order-time-select"
                  className="w-32 bg-[#050505] border border-[#2a2a2a] rounded-sm px-2 h-10 text-sm"
                >
                  {!TIME_SLOTS.some((t) => t.value === timePart) && <option value={timePart}>{timePart}</option>}
                  {TIME_SLOTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </FormField>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="label-eyebrow">Menu Selection</Label>
              <div className="flex items-center gap-2">
                {packages.length > 0 && (
                  <select
                    onChange={(e) => {
                      const p = packages.find((x) => x.id === e.target.value);
                      if (!p) return;
                      // Open the dish picker with every package dish pre-checked
                      const checked = {};
                      p.items.forEach((pi) => { checked[pi.menu_item_id] = true; });
                      setPkgPick({ pkg: p, checked });
                      e.target.value = "";
                    }}
                    data-testid="apply-package-select"
                    className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-8 text-xs"
                  >
                    <option value="">+ Apply package…</option>
                    {packages.map((p) => <option key={p.id} value={p.id}>{p.name} (RM{p.price_per_pax}/{p.pricing_mode === "set" ? "set" : "pax"})</option>)}
                  </select>
                )}
                <Button type="button" variant="outline" onClick={addItem} className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] h-8" data-testid="add-item-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                </Button>
              </div>
            </div>

            {/* Package dish picker — choose which dishes from the package to include */}
            {pkgPick && (
              <div className="mb-3 p-3 rounded-sm border border-[#D4AF37]/40 bg-[#0f0d08]" data-testid="pkg-dish-picker">
                <div className="text-xs text-[#D4AF37] mb-2">
                  Choose dishes from "{pkgPick.pkg.name}" ({Object.values(pkgPick.checked).filter(Boolean).length}/{pkgPick.pkg.items.length} selected)
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                  {pkgPick.pkg.items.map((pi) => {
                    const it = catalog.find((x) => x.id === pi.menu_item_id);
                    if (!it) return null;
                    const on = !!pkgPick.checked[pi.menu_item_id];
                    return (
                      <label key={pi.menu_item_id} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer hover:text-white py-0.5">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => setPkgPick((s) => ({ ...s, checked: { ...s.checked, [pi.menu_item_id]: !on } }))}
                          data-testid={`pkg-dish-${pi.menu_item_id}`}
                          className="accent-[#D4AF37]"
                        />
                        <span>{it.name}{it.name_cn ? ` (${it.name_cn})` : ""}</span>
                        <span className="text-[10px] text-zinc-500 ml-auto">{it.category}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <Button type="button" variant="outline" onClick={() => setPkgPick(null)} className="border-[#2a2a2a] h-8 text-xs">Cancel</Button>
                  <Button type="button" onClick={applyPackage} data-testid="pkg-apply-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] h-8 text-xs">
                    Apply selected dishes
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Input
                value={dishQ}
                onChange={(e) => setDishQ(e.target.value)}
                placeholder="Search dish to filter the dropdowns… (name / 中文)"
                data-testid="order-dish-search"
                className="bg-[#050505] border-[#2a2a2a] h-8 text-xs"
              />
              {form.items.map((it, i) => {
                const prevPkg = i > 0 ? (form.items[i - 1].package_name || "") : null;
                const curPkg = it.package_name || "";
                const showHeader = curPkg !== prevPkg;
                return (
                <React.Fragment key={i}>
                  {showHeader && (
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] pt-1.5 flex items-center gap-1.5" data-testid={`pkg-group-${i}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> {curPkg || "Custom items"}
                    </div>
                  )}
                  <div className="grid grid-cols-12 gap-2 items-center">
                  <select
                    value={catalog.find((c) => c.name === it.name)?.id || ""}
                    onChange={(e) => {
                      const c = catalog.find((x) => x.id === e.target.value);
                      if (c) {
                        updateItem(i, "name", c.name);
                        updateItem(i, "category", c.category);
                        const min = Math.max(1, c.min_order_qty || 1);
                        if ((Number(it.quantity) || 0) < min) updateItem(i, "quantity", min);
                      }
                    }}
                    data-testid={`item-catalog-${i}`}
                    className="col-span-4 bg-[#050505] border border-[#2a2a2a] rounded-sm px-2 h-9 text-sm"
                  >
                    <option value="">{it.name || (filteredCatalog.length ? "— Pick from menu —" : "— No dish matches search —")}</option>
                    {filteredCatalog.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{c.name_cn ? ` (${c.name_cn})` : ""} · RM{c.unit_price}{(c.min_order_qty || 1) > 1 ? ` · min ${c.min_order_qty}` : ""}</option>
                    ))}
                  </select>
                  <select value={it.category} onChange={(e) => updateItem(i, "category", e.target.value)} className="col-span-2 bg-[#050505] border border-[#2a2a2a] rounded-sm px-2 h-9 text-sm">
                    {categories.length > 0
                      ? categories.map((c) => <option key={c.key} value={c.key}>{c.name_en}</option>)
                      : ["main", "side", "soup", "dessert", "drinks", "appetizer", "salad", "noodles", "rice", "snack", "station"].map((c) => <option key={c} value={c}>{c}</option>)}
                    {it.category && categories.length > 0 && !categories.find((c) => c.key === it.category) && (
                      <option value={it.category}>{it.category}</option>
                    )}
                  </select>
                  <Input type="number" placeholder="qty" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="col-span-1 bg-[#050505] border-[#2a2a2a] h-9 px-1.5" />
                  <select value={it.uom || "pax"} onChange={(e) => updateItem(i, "uom", e.target.value)} data-testid={`item-uom-${i}`} className="col-span-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-1 h-9 text-xs capitalize">
                    {UOMS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <Input placeholder="remark (e.g. less spicy)" value={it.notes || ""} onChange={(e) => updateItem(i, "notes", e.target.value)} data-testid={`item-remark-${i}`} className="col-span-3 bg-[#050505] border-[#2a2a2a] h-9" />
                  <Button type="button" variant="ghost" onClick={() => removeItem(i)} className="col-span-1 h-9 text-zinc-500 hover:text-red-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                </React.Fragment>
                );
              })}
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">Pick from your saved menu (manage in <a href="/menu" className="text-[#D4AF37] hover:underline">Menu page</a>) or click + Add for a custom one-off item.</p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <FormField label="Pax (guests)">
              <Input type="number" min="0" value={form.pax} onChange={(e) => set("pax", e.target.value)} placeholder="0" data-testid="order-pax" className="bg-[#050505] border-[#2a2a2a]" />
            </FormField>
            <FormField label={`Total Price (RM)${form.package_name && form.package_name !== "Custom" ? ` — from "${form.package_name}"` : ""}`}>
              <Input type="number" min="0" step="0.01" value={form.total_amount} onChange={(e) => set("total_amount", e.target.value)} placeholder="0.00" data-testid="order-total-amount" className="bg-[#050505] border-[#2a2a2a]" />
            </FormField>
            <FormField label="Delivery Fee (RM)">
              <Input type="number" min="0" step="0.01" value={form.delivery_fee} onChange={(e) => set("delivery_fee", e.target.value)} placeholder="0.00" data-testid="order-delivery-fee" className="bg-[#050505] border-[#2a2a2a]" />
            </FormField>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-[#1a1a1a]">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#2a2a2a]">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="order-save-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B]">
              {saving ? "Saving…" : (editing ? "Save Changes" : "Create Order")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, wide, children }) {
  return (
    <div className={`space-y-1.5 ${wide ? "col-span-2" : ""}`}>
      <Label className="label-eyebrow text-[10px]">{label}</Label>
      {children}
    </div>
  );
}
